import { formatarMoeda, formatarNumero } from '@persona/ui';
import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight, CircleCheck } from 'lucide-react';
import { api } from '../src/api/cliente';
import { useSessao } from '../src/sessao/sessao';

/** Número de destaque: abre a rotina onde ele se explica. */
export interface NumeroDia { modulo: string; rotulo: string; valor: string; detalhe?: string; rotina: string }

/** Algo que pede ação; só entra quando há o que fazer. */
export interface PendenciaDia { modulo: string; texto: string; quantidade: number; valor?: number; rotina: string; alerta?: boolean }

interface Faixas { vencido: number; quantidadeVencidos: number; hoje: number }
interface PainelFin { saldoContas: number; receber: Faixas; pagar: Faixas; pendencias: { aprovacoesPagamento: number; valorAprovacoesPagamento: number; movimentosNaoConciliados: number } }
interface PainelFat {
  mes: { periodo: string; liquido: number; pedidos: number }; mesAnterior: { periodo: string; liquido: number };
  carteira: { pedidosBloqueados: number; valorBloqueado: number; pedidosLiberados: number; valorLiberado: number; orcamentosAbertos: number; valorOrcamentos: number };
}
interface PainelEst { valorEstoque: number; produtosComSaldo: number; pendencias: { paraRepor: number; abaixoMinimo: number; lotesVencidos: number; inventariosAbertos: number } }
interface Contagem { quantidade: number; valor: number }
interface PainelCom { mes: { valor: number; pedidos: number }; carteira: { solicitacoesAprovar: number; pedidosAprovar: Contagem; pedidosReceber: Contagem; pedidosAtrasados: Contagem } }

interface Partes { numeros: NumeroDia[]; pendencias: PendenciaDia[] }

const vazio: Partes = { numeros: [], pendencias: [] };

const plural = (n: number, um: string, varios: string) => `${formatarNumero(n)} ${n === 1 ? um : varios}`;

/** Só entra a pendência com quantidade; o resto é ruído na tela de quem abre o sistema de manhã. */
function pendencias(lista: (PendenciaDia | false)[]): PendenciaDia[] {
  return lista.filter((p): p is PendenciaDia => !!p && p.quantidade > 0);
}

function vendas(p: PainelFat): Partes {
  const c = p.carteira;
  const anterior = p.mesAnterior.liquido;
  const variacao = anterior > 0 ? `${p.mes.liquido >= anterior ? '+' : '−'}${formatarNumero(Math.abs(((p.mes.liquido - anterior) / anterior) * 100), 1)}% sobre o mês anterior` : undefined;
  return {
    numeros: [
      { modulo: 'FAT', rotulo: 'Vendas no mês', valor: formatarMoeda(p.mes.liquido), detalhe: variacao ?? plural(p.mes.pedidos, 'pedido faturado', 'pedidos faturados'), rotina: 'FAT000' },
      { modulo: 'FAT', rotulo: 'Pronto para faturar', valor: formatarMoeda(c.valorLiberado), detalhe: plural(c.pedidosLiberados, 'pedido liberado', 'pedidos liberados'), rotina: 'FAT090' },
    ],
    pendencias: pendencias([
      { modulo: 'FAT', texto: c.pedidosBloqueados === 1 ? 'pedido bloqueado por crédito ou estoque' : 'pedidos bloqueados por crédito ou estoque', quantidade: c.pedidosBloqueados, valor: c.valorBloqueado, rotina: 'FAT130', alerta: true },
      { modulo: 'FAT', texto: c.pedidosLiberados === 1 ? 'pedido liberado esperando faturamento' : 'pedidos liberados esperando faturamento', quantidade: c.pedidosLiberados, valor: c.valorLiberado, rotina: 'FAT090' },
      { modulo: 'FAT', texto: c.orcamentosAbertos === 1 ? 'orçamento em aberto' : 'orçamentos em aberto', quantidade: c.orcamentosAbertos, valor: c.valorOrcamentos, rotina: 'FAT050' },
    ]),
  };
}

function financeiro(p: PainelFin): Partes {
  return {
    numeros: [
      { modulo: 'FIN', rotulo: 'Saldo em contas', valor: formatarMoeda(p.saldoContas), rotina: 'FIN210' },
      { modulo: 'FIN', rotulo: 'A receber hoje', valor: formatarMoeda(p.receber.hoje), detalhe: p.receber.vencido > 0 ? `${formatarMoeda(p.receber.vencido)} vencido` : 'nada vencido', rotina: 'FIN060' },
      { modulo: 'FIN', rotulo: 'A pagar hoje', valor: formatarMoeda(p.pagar.hoje), detalhe: p.pagar.vencido > 0 ? `${formatarMoeda(p.pagar.vencido)} vencido` : 'nada vencido', rotina: 'FIN100' },
    ],
    pendencias: pendencias([
      { modulo: 'FIN', texto: p.receber.quantidadeVencidos === 1 ? 'título a receber vencido' : 'títulos a receber vencidos', quantidade: p.receber.quantidadeVencidos, valor: p.receber.vencido, rotina: 'FIN060', alerta: true },
      { modulo: 'FIN', texto: p.pagar.quantidadeVencidos === 1 ? 'título a pagar vencido' : 'títulos a pagar vencidos', quantidade: p.pagar.quantidadeVencidos, valor: p.pagar.vencido, rotina: 'FIN100', alerta: true },
      { modulo: 'FIN', texto: p.pendencias.aprovacoesPagamento === 1 ? 'pagamento aguardando aprovação' : 'pagamentos aguardando aprovação', quantidade: p.pendencias.aprovacoesPagamento, valor: p.pendencias.valorAprovacoesPagamento, rotina: 'FIN100' },
      { modulo: 'FIN', texto: p.pendencias.movimentosNaoConciliados === 1 ? 'movimento bancário sem conciliar' : 'movimentos bancários sem conciliar', quantidade: p.pendencias.movimentosNaoConciliados, rotina: 'FIN140' },
    ]),
  };
}

function estoque(p: PainelEst): Partes {
  return {
    numeros: [{ modulo: 'EST', rotulo: 'Valor em estoque', valor: formatarMoeda(p.valorEstoque), detalhe: plural(p.produtosComSaldo, 'produto com saldo', 'produtos com saldo'), rotina: 'EST000' }],
    pendencias: pendencias([
      { modulo: 'EST', texto: p.pendencias.paraRepor === 1 ? 'produto para repor' : 'produtos para repor', quantidade: p.pendencias.paraRepor, rotina: 'EST110', alerta: p.pendencias.abaixoMinimo > 0 },
      { modulo: 'EST', texto: p.pendencias.lotesVencidos === 1 ? 'lote vencido com saldo' : 'lotes vencidos com saldo', quantidade: p.pendencias.lotesVencidos, rotina: 'EST120', alerta: true },
      { modulo: 'EST', texto: p.pendencias.inventariosAbertos === 1 ? 'inventário em aberto' : 'inventários em aberto', quantidade: p.pendencias.inventariosAbertos, rotina: 'EST060' },
    ]),
  };
}

function compras(p: PainelCom): Partes {
  const c = p.carteira;
  return {
    numeros: [{ modulo: 'COM', rotulo: 'Compras no mês', valor: formatarMoeda(p.mes.valor), detalhe: plural(p.mes.pedidos, 'pedido', 'pedidos'), rotina: 'COM000' }],
    pendencias: pendencias([
      { modulo: 'COM', texto: c.pedidosAtrasados.quantidade === 1 ? 'entrega de fornecedor atrasada' : 'entregas de fornecedor atrasadas', quantidade: c.pedidosAtrasados.quantidade, valor: c.pedidosAtrasados.valor, rotina: 'COM090', alerta: true },
      { modulo: 'COM', texto: c.pedidosAprovar.quantidade === 1 ? 'pedido de compra a aprovar' : 'pedidos de compra a aprovar', quantidade: c.pedidosAprovar.quantidade, valor: c.pedidosAprovar.valor, rotina: 'COM060' },
      { modulo: 'COM', texto: c.solicitacoesAprovar === 1 ? 'solicitação de compra a aprovar' : 'solicitações de compra a aprovar', quantidade: c.solicitacoesAprovar, rotina: 'COM030' },
      { modulo: 'COM', texto: c.pedidosReceber.quantidade === 1 ? 'pedido de compra a receber' : 'pedidos de compra a receber', quantidade: c.pedidosReceber.quantidade, valor: c.pedidosReceber.valor, rotina: 'COM070' },
    ]),
  };
}

const FONTES = [
  { modulo: 'FAT', url: '/fat/painel-comercial', montar: (d: unknown) => vendas(d as PainelFat) },
  { modulo: 'FIN', url: '/fin/painel', montar: (d: unknown) => financeiro(d as PainelFin) },
  { modulo: 'EST', url: '/est/painel', montar: (d: unknown) => estoque(d as PainelEst) },
  { modulo: 'COM', url: '/com/painel', montar: (d: unknown) => compras(d as PainelCom) },
];

/**
 * Números e pendências do dia a partir dos painéis dos módulos (FAT000, FIN000, EST000, COM000), na filial e data-base atuais.
 * Só consulta os módulos que o usuário tem; painel sem permissão (403) ou fora do ar some em silêncio em vez de quebrar o início.
 */
export function useResumoDia(modulos: string[] | undefined, apenas?: string) {
  const { empresa, filial, dataBase } = useSessao();
  const fontes = FONTES.filter((f) => modulos?.includes(f.modulo) && (!apenas || f.modulo === apenas));
  const resultados = useQueries({
    queries: fontes.map((f) => ({
      queryKey: ['resumo-dia', f.modulo, empresa?.codigo, filial?.codigo, dataBase],
      queryFn: ({ signal }: { signal: AbortSignal }) => api.get<unknown>(f.url, signal),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const partes = resultados.map((r, i) => (r.data ? fontes[i].montar(r.data) : vazio));
  return {
    carregando: modulos === undefined || resultados.some((r) => r.isLoading),
    numeros: partes.flatMap((p) => p.numeros),
    pendencias: partes.flatMap((p) => p.pendencias).sort((a, b) => Number(!!b.alerta) - Number(!!a.alerta)),
    consultou: fontes.length > 0,
  };
}

export function NumerosDia({ numeros }: { numeros: NumeroDia[] }) {
  if (numeros.length === 0) return null;
  return (
    <div className="dia-numeros">
      {numeros.map((n) => (
        <Link key={`${n.modulo}-${n.rotulo}`} to="/r/$rotina" params={{ rotina: n.rotina }} className="dia-numero painel">
          <span className="dia-numero-rotulo">{n.rotulo}</span>
          <strong className="dia-numero-valor" data-zerado={/^R\$\s?0,00$|^0$/.test(n.valor) || undefined}>{n.valor}</strong>
          {n.detalhe && <span className="dia-numero-detalhe">{n.detalhe}</span>}
        </Link>
      ))}
    </div>
  );
}

export function PendenciasDia({ pendencias, nomeModulo }: { pendencias: PendenciaDia[]; nomeModulo?: (codigo: string) => string | undefined }) {
  if (pendencias.length === 0) {
    return (
      <p className="dia-em-dia"><CircleCheck size={16} aria-hidden="true" /> Nada pendente por aqui. Pedidos, títulos, estoque e compras estão em dia.</p>
    );
  }
  return (
    <ul className="dia-pendencias" data-sem-modulo={nomeModulo ? undefined : true}>
      {pendencias.map((p) => (
        <li key={`${p.rotina}-${p.texto}`}>
          <Link to="/r/$rotina" params={{ rotina: p.rotina }} className="dia-pendencia" data-alerta={p.alerta || undefined}>
            <span className="dia-pendencia-qtd">{formatarNumero(p.quantidade)}</span>
            <span className="dia-pendencia-texto">{p.texto}</span>
            {nomeModulo && <span className="dia-pendencia-modulo">{nomeModulo(p.modulo) ?? ''}</span>}
            <span className="dia-pendencia-valor">{p.valor !== undefined && p.valor > 0 ? formatarMoeda(p.valor) : ''}</span>
            <ChevronRight size={14} className="dia-pendencia-seta" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
